import calendlyLogo from "@/assets/integrations/calendly.svg";
import elevenlabsLogo from "@/assets/integrations/elevenlabs.svg";
import googleLogo from "@/assets/integrations/google.svg";
import microsoftLogo from "@/assets/integrations/microsoft.svg";
import mistralLogo from "@/assets/integrations/mistral.svg";
import openaiLogo from "@/assets/integrations/openai.svg";
import provallianceLogo from "@/assets/integrations/provalliance.svg";
import {
  BrowserIcon,
  CalendarCheckIcon,
  VideoCameraIcon,
  CalendarIcon,
  IdentificationCardIcon,
  LinkIcon,
  WaveformIcon,
  type Icon,
} from "@phosphor-icons/react";

/**
 * Every third-party system the back-office names, with its logo — the booking
 * software the agent books through (`booking.integration`), the accounts a tenant
 * connects (`external_accounts.provider`) and the speech providers behind a voice
 * (`voices.provider_name`). One table so a brand looks the same on every screen
 * that shows it.
 *
 * A brand is drawn from its own file in `assets/integrations/`; a protocol with no
 * brand of its own (CalDAV, an ICS feed, our own calendar) gets an icon on a tinted
 * tile instead.
 */
type IntegrationId =
  // booking software
  | "internal"
  | "virtual_browser"
  | "calendly"
  | "provalliance"
  | "provalliance_backoffice"
  // connected accounts
  | "website"
  | "google_meet"
  | "google"
  | "microsoft"
  | "caldav"
  | "carddav"
  | "dav"
  | "ics"
  // speech providers
  | "elevenlabs"
  | "openai"
  | "mistral"
  | "cartesia";

/**
 * What a booking integration is, for the picker's filter:
 * - `builtin`: our own calendar;
 * - `scheduling`: an online appointment tool (Calendly, Cal.com, Acuity…);
 * - `salon`: a salon / institute management software (Provalliance, Planity…);
 * - `website`: any booking website, driven by the virtual browser.
 */
export type BookingCategory = "builtin" | "scheduling" | "salon" | "website";

interface IntegrationBrand {
  id: IntegrationId;
  /**
   * The name in English, and the only one for a brand (which is never translated).
   * The few that are not brands (`internal`, `virtual_browser`) are translated under
   * `tools.integrations.<id>.name`: read it through `useIntegrationName`.
   */
  name: string;
  logo: { src: string } | { icon: Icon; tint: string };
  /** Set on the booking integrations only. */
  bookingCategory?: BookingCategory;
}

const INTEGRATIONS: Record<IntegrationId, IntegrationBrand> = {
  internal: {
    id: "internal",
    name: "HeyUp calendar",
    logo: { icon: CalendarCheckIcon, tint: "primary" },
    bookingCategory: "builtin",
  },
  virtual_browser: {
    id: "virtual_browser",
    name: "Virtual browser",
    logo: { icon: BrowserIcon, tint: "violet" },
    bookingCategory: "website",
  },
  calendly: {
    id: "calendly",
    name: "Calendly",
    logo: { src: calendlyLogo },
    bookingCategory: "scheduling",
  },
  provalliance: {
    id: "provalliance",
    name: "Provalliance",
    logo: { src: provallianceLogo },
    bookingCategory: "salon",
  },
  provalliance_backoffice: {
    id: "provalliance_backoffice",
    name: "Provalliance",
    logo: { src: provallianceLogo },
    bookingCategory: "salon",
  },
  // The booking-website account (provider `website`): the virtual browser's.
  website: {
    id: "website",
    name: "Website",
    logo: { icon: BrowserIcon, tint: "violet" },
    bookingCategory: "website",
  },
  google: { id: "google", name: "Google", logo: { src: googleLogo } },
  // Not an account of its own: the way into a Google account with its calendar,
  // which is what creates the meetings.
  google_meet: {
    id: "google_meet",
    name: "Google Meet",
    logo: { icon: VideoCameraIcon, tint: "sky" },
  },
  microsoft: {
    id: "microsoft",
    name: "Microsoft",
    logo: { src: microsoftLogo },
  },
  caldav: {
    id: "caldav",
    name: "CalDAV",
    logo: { icon: CalendarIcon, tint: "sky" },
  },
  carddav: {
    id: "carddav",
    name: "CardDAV",
    logo: { icon: IdentificationCardIcon, tint: "sky" },
  },
  dav: { id: "dav", name: "DAV", logo: { icon: CalendarIcon, tint: "sky" } },
  ics: { id: "ics", name: "ICS", logo: { icon: LinkIcon, tint: "amber" } },
  elevenlabs: {
    id: "elevenlabs",
    name: "ElevenLabs",
    logo: { src: elevenlabsLogo },
  },
  openai: { id: "openai", name: "OpenAI", logo: { src: openaiLogo } },
  mistral: { id: "mistral", name: "Mistral", logo: { src: mistralLogo } },
  cartesia: {
    id: "cartesia",
    name: "Cartesia",
    logo: { icon: WaveformIcon, tint: "amber" },
  },
};

export const getIntegration = (id: string | undefined | null) =>
  id && id in INTEGRATIONS ? INTEGRATIONS[id as IntegrationId] : undefined;
